import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, Autocomplete, Avatar, IconButton, Paper, Chip, CircularProgress } from '@mui/material';
import { Description } from '@mui/icons-material';
import { MdPerson, MdClose, MdSearch, MdPersonSearch } from 'react-icons/md';
import { useTheme } from '@mui/material/styles';
import { apiCall } from '../../utils/apiUtils';
// No longer using field standardization
import './QuickEmployeeSearch.css';
import pxToRem from '../../Themes/Functions/pxToRem';
import AppService from '../../services/AppService';
import AuthenticatedImage from '../../Components/AuthenticatedImage';
import debounce from 'lodash/debounce';

/**
 * QuickEmployeeSearch component for ticket creation
 * Provides a streamlined search interface for finding employees
 * 
 * @param {Object} props
 * @param {Function} props.onEmployeeSelect - Callback when employee is selected
 * @param {Object} props.selectedEmployee - Currently selected employee
 * @param {boolean} props.required - Whether employee selection is required
 * @param {string} props.organizationId - Optional organization ID to filter employees
 */
const QuickEmployeeSearch = ({ onEmployeeSelect, selectedEmployee, required = false, organizationId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  
  // Store the selected employee
  const [selected, setSelected] = useState(selectedEmployee || null);
  
  // Create a debounced search function
  const debouncedSearch = useRef(
    debounce(async (term, orgId) => {
      if (!term || term.length < 2) {
        setEmployees([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Searching for term:', term);
        
        // Build query parameters
        let queryParams = `query=${encodeURIComponent(term)}&includePhoto=true`;
        
        // Add organizationId if provided (for on-behalf ticket filtering)
        if (orgId) {
          queryParams += `&organizationId=${encodeURIComponent(orgId)}`;
          console.log('Filtering by organizationId:', orgId);
        }
        
        // Make the API call to the new endpoint that returns StaticFieldCode-aligned field names
        // Always include photo to support auto-filling functionality
        const response = await AppService.get(`v1/employees/search-for-ticket?${queryParams}`);
        
        // Process API response to extract employee data
        let employeeData = [];
        
        if (response && response.success) {
          console.log('Raw API response structure:', JSON.stringify(response, null, 2));
          
          // Check if response.data is an array
          if (Array.isArray(response.data)) {
            console.log('Found direct array in response.data');
            employeeData = response.data;
          } 
          // Check if response itself is the data
          else if (Array.isArray(response)) {
            console.log('Response itself is an array');
            employeeData = response;
          }
          // Handle other response formats
          else if (response.data && typeof response.data === 'object') {
            // Check if it has a nested data array
            if (response.data.data && Array.isArray(response.data.data)) {
              console.log('Found nested data array');
              employeeData = response.data.data;
            } 
            // Check if it's a single employee object
            else if (!Array.isArray(response.data) && !response.data.success) {
              console.log('Found single employee object');
              employeeData = [response.data];
            }
            // Last resort - try to extract from a non-standard format
            else {
              console.log('Attempting to extract from non-standard format');
              const possibleArrays = Object.values(response.data).filter(val => Array.isArray(val));
              if (possibleArrays.length > 0) {
                // Use the first array found
                employeeData = possibleArrays[0];
                console.log('Found array in response.data');
              }
            }
          }
          
          // Debug the first employee's photo if available
          if (employeeData.length > 0 && employeeData[0].photo) {
            const samplePhoto = employeeData[0].photo;
            console.log('Sample photo URL:', samplePhoto);
            console.log('Photo URL type:', typeof samplePhoto);
            console.log('Photo URL starts with data:image?', samplePhoto.startsWith('data:image'));
          }
          
          // Clean up photo URLs - remove incorrect data:image/jpeg;base64, prefix
          employeeData = employeeData.map(employee => {
            if (employee.photo && typeof employee.photo === 'string' && 
                employee.photo.startsWith('data:image/jpeg;base64,/v1/users/')) {
              console.log('Cleaning photo URL for employee:', employee.uid || employee.employeeId);
              employee.photo = employee.photo.replace('data:image/jpeg;base64,', '');
              console.log('Cleaned photo URL:', employee.photo);
            }
            return employee;
          });
          
          console.log('Final extracted employee data:', employeeData);
          console.log('Number of employees found:', employeeData.length);
          
          // Set the employees state with processed data
          setEmployees(employeeData);
          
          // Force open the dropdown if we have results
          if (employeeData.length > 0) {
            setOpen(true);
          }
        } else {
          console.log('No valid response received');
          setEmployees([]);
        }
        
        setError('');
      } catch (error) {
        console.error('Error searching employees:', error);
        setError('Failed to search employees');
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    }, 500)
  ).current;
  
  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);
  
  // Update search when term or organizationId changes
  useEffect(() => {
    debouncedSearch(searchTerm, organizationId);
  }, [searchTerm, organizationId]);
  
  // Update local state when selectedEmployee prop changes
  useEffect(() => {
    if (selectedEmployee) {
      setSelected(selectedEmployee);
    }
  }, [selectedEmployee]);
  
  const handleEmployeeSelect = (event, employee) => {
    console.log('=== HANDLE EMPLOYEE SELECT CALLED ===');
    
    if (!employee) {
      console.warn('No employee provided to handleEmployeeSelect');
      return;
    }
    
    console.log('Employee selected in QuickEmployeeSearch:', employee);
    
    // Process the employee data before setting it
    if (employee) {
      // Log the employee data for debugging
      console.log('Employee data:', JSON.stringify(employee));
      
      // DEBUG: Log UID and employeeId specifically
      console.log('DEBUG: Employee UID:', employee.uid);
      console.log('DEBUG: Employee employeeId:', employee.employeeId);
      console.log('DEBUG: Employee id:', employee.id);
      
      // Ensure we have a valid UID
      if (!employee.uid && employee.employeeId) {
        console.warn('WARNING: Employee missing uid, using employeeId as fallback:', employee.employeeId);
      } else if (!employee.uid && !employee.employeeId) {
        console.error('ERROR: Employee missing both uid and employeeId:', employee);
      }
      
      // Log the mobile number if it exists
      if (employee.mobileNumber) {
        console.log('Mobile number from employee data:', employee.mobileNumber);
      } else {
        console.log('No mobile number found in employee data');
      }
      
      // Log document information if available
      if (employee.documents && employee.documents.length > 0) {
        console.log('Employee documents:', employee.documents);
        console.log('Document types available:', employee.documents.map(doc => doc.documentType || doc.type).join(', '));
      } else {
        console.log('No documents found in employee data');
      }
      
      // Use the employee data as is since field names already match
      const processedEmployee = {...employee};
      
      // Process photo if available
      if (processedEmployee.photo) {
        if (typeof processedEmployee.photo === 'string') {
          // If it's already a data URL or blob URL, leave it as is
          if (!processedEmployee.photo.startsWith('data:') && !processedEmployee.photo.startsWith('blob:')) {
            // Otherwise assume it's a base64 string or a path and convert to data URL
            processedEmployee.photo = processedEmployee.photo.startsWith('/') ?
              processedEmployee.photo : `data:image/jpeg;base64,${processedEmployee.photo}`;
          }
        }
      }
      
      // Process documents if available
      if (processedEmployee.documents && Array.isArray(processedEmployee.documents)) {
        // Ensure all document metadata is properly formatted
        processedEmployee.documents = processedEmployee.documents.map(doc => ({
          ...doc,
          // Ensure consistent property names
          documentType: doc.documentType || doc.type || 'unknown',
          fileName: doc.fileName || doc.name || 'unnamed',
          createdAt: doc.createdAt || doc.uploadDate || new Date().toISOString()
        }));
      }
      
      console.log('Standardized employee data:', processedEmployee);
      console.log('Mobile number after processing:', processedEmployee.mobileNumber);
      setSelected(processedEmployee);
      
      // Pass the processed employee to the parent component
      if (onEmployeeSelect) {
        onEmployeeSelect(processedEmployee);
      }
    } else {
      setSelected(null);
      if (onEmployeeSelect) {
        onEmployeeSelect(null);
      }
    }
  };
  
  const handleClearSelection = () => {
    console.log('Clearing employee selection');
    setSelected(null);
    setSearchTerm('');
    
    // Notify parent component that selection has been cleared
    if (onEmployeeSelect) {
      console.log('Notifying parent component of cleared selection');
      onEmployeeSelect(null);
    }
  };
  
  // Render the selected employee display
  const renderSelectedEmployee = () => {
    if (!selected) return null;
    
    console.log('Rendering selected employee:', selected);
    console.log('Selected employee photo available:', !!selected.photo);
    
    // More detailed photo logging
    if (selected.photo) {
      console.log('Selected photo type:', typeof selected.photo);
      console.log('Selected photo length:', selected.photo.length);
      if (typeof selected.photo === 'string') {
        console.log('Selected photo starts with data:', selected.photo.startsWith('data:'));
        console.log('Selected photo preview:', selected.photo.substring(0, 50) + '...');
      }
    }
    
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        p: 2.5, 
        border: '1px solid #e1f5fe',
        borderRadius: '10px',
        backgroundColor: '#f5f9fc',
        mt: 2,
        boxShadow: '0 2px 8px rgba(0,131,143,0.08)',
        transition: 'all 0.3s ease'
      }}>
        <Box sx={{ position: 'relative', width: 52, height: 52, mr: 2.5 }}>
          {(() => {
            // Prefer the inline base64 thumbnail (zero extra HTTP fetch) when
            // the backend has populated it. Falls back to the full-image URLs
            // only when the row predates the inline-thumb feature (i.e. has
            // no t_employee_document.photo_thumbnail yet — run the backend
            // backfill endpoint to populate those).
            if (selected.photoThumbnail) {
              return (
                <img
                  src={selected.photoThumbnail}
                  alt={`${selected.nameEn || selected.firstNameEn || 'Employee'} photo`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)',
                  }}
                />
              );
            }

            // Legacy fallbacks below — preserved for rows without an inline
            // thumbnail yet. Once the backfill has run on prod, these
            // branches become dead code and can be removed.
            const normalizeDocType = (t) => String(t || '').toLowerCase().replace(/_/g, '');
            const minifiedPhotoDoc = selected.documents?.find(doc => normalizeDocType(doc.documentType) === 'photominified');
            const regularPhotoDoc = selected.documents?.find(doc => normalizeDocType(doc.documentType) === 'photo');

            // First try to use minifiedPhoto field directly from API
            if (selected.minifiedPhoto && selected.uid) {
              console.log('Using minified photo field for selected employee:', selected.minifiedPhoto);
              
              // More permissive UID validation - accept any non-empty string
              const isValidUID = selected.uid && typeof selected.uid === 'string' && selected.uid.trim().length > 0;
              
              if (!isValidUID) {
                console.warn(`Invalid UID format for selected employee minified photo: ${selected.uid}. Skipping photo display.`);
                return (
                  <Box
                    sx={{
                      backgroundColor: 'red',
                      color: 'white',
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%'
                    }}
                  >
                    INVALID UID
                  </Box>
                );
              }
              
              return (
                <AuthenticatedImage
                  src={selected.minifiedPhoto}
                  employeeId={selected.uid} // Pass UID as employeeId for proper authentication
                  alt={`${selected.nameEn || selected.firstNameEn || 'Employee'} photo (minified)`}
                  maxSize={0} // No client-side minification needed for server-minified photos
                  onLoad={() => {
                    console.log(`Successfully loaded minified photo for selected employee ${selected.employeeId || selected.uid}`);
                  }}
                  onError={(err) => {
                    console.error(`Error loading minified photo for selected employee ${selected.employeeId || selected.uid}:`, err);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)'
                  }}
                />
              );
            }
            // Then check if we have a minified photo document
            else if (minifiedPhotoDoc && selected.uid) {
              console.log('Using minified photo document for selected employee:', minifiedPhotoDoc);
              
              // More permissive UID validation - accept any non-empty string
              const isValidUID = selected.uid && typeof selected.uid === 'string' && selected.uid.trim().length > 0;
              
              if (!isValidUID) {
                console.warn(`Invalid UID format for selected employee minified photo document: ${selected.uid}. Skipping photo display.`);
                return (
                  <Box
                    sx={{
                      backgroundColor: 'red',
                      color: 'white',
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%'
                    }}
                  >
                    INVALID UID
                  </Box>
                );
              }
              
              // Use the minified photo document for display with a relative API path
              const photoUrl = `/v1/users/${selected.uid}/documents/photo_minified/download`;
              console.log('Using selected employee minified photo document URL:', photoUrl, 'for UID:', selected.uid);
              
              return (
                <AuthenticatedImage
                  src={photoUrl}
                  employeeId={selected.uid} // Pass UID as employeeId for proper authentication
                  alt={`${selected.nameEn || selected.firstNameEn || 'Employee'} photo (minified)`}
                  maxSize={0} // No client-side minification needed for server-minified photos
                  onLoad={() => {
                    console.log(`Successfully loaded minified photo document for selected employee ${selected.employeeId || selected.uid}`);
                  }}
                  onError={(err) => {
                    console.error(`Error loading minified photo document for selected employee ${selected.employeeId || selected.uid}:`, err);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)'
                  }}
                />
              );
            }
            // Then try to use the regular photo field
            else if (selected.photo && selected.uid) {
              // Add detailed logging for selected employee UID
              console.log('DEBUG: Selected employee UID details:', {
                uid: selected.uid,
                uidType: typeof selected.uid,
                uidLength: selected.uid ? selected.uid.length : 0,
                employeeId: selected.employeeId,
                id: selected.id
              });
              
              // More permissive UID validation - accept any non-empty string
              const isValidUID = selected.uid && typeof selected.uid === 'string' && selected.uid.trim().length > 0;
              
              if (!isValidUID) {
                console.warn(`Invalid UID format for selected employee photo: ${selected.uid}. Skipping photo display.`);
                return (
                  <Box
                    sx={{
                      backgroundColor: 'red',
                      color: 'white',
                      fontSize: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%'
                    }}
                  >
                    INVALID UID
                  </Box>
                );
              }
              
              console.log('Using selected employee photo field:', selected.photo, 'for UID:', selected.uid);
              
              return (
                <AuthenticatedImage
                  src={selected.photo}
                  employeeId={selected.uid} // Pass UID as employeeId for proper authentication
                  alt={`${selected.nameEn || selected.firstNameEn || 'Employee'} photo`}
                  maxSize={100} // Minify to 100px max dimension for selected employee display
                  onLoad={() => {
                    console.log(`Successfully loaded photo field for selected employee ${selected.employeeId || selected.uid}`);
                  }}
                  onError={(err) => {
                    console.error(`Error loading photo field for selected employee ${selected.employeeId || selected.uid}:`, err);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.1)'
                  }}
                />
              );
            } else {
              // DEBUG: No fallback - force showing photo or nothing to debug the issue
              console.error('DEBUG: No photo document found for selected employee:', selected);
              console.error('DEBUG: Available documents:', selected.documents);
              return (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    backgroundColor: '#ff0000', // Red background to make it obvious
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textAlign: 'center'
                  }}
                >
                  NO PHOTO
                </Box>
              );
            }
          })()}
        </Box>
        <Box>
          <Typography sx={{ 
            fontSize: '1.1rem', 
            fontWeight: 600,
            letterSpacing: '0.01em',
            color: '#263238',
            mb: 0.5
          }}>
            {selected.nameEn || `${selected.firstNameEn || selected.firstName || ''} ${selected.lastNameEn || selected.lastName || ''}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 0.8 }}>
            {(selected.employeeId || selected.id) && (
              <Chip 
                size="small" 
                label={`ID: ${selected.employeeId || selected.id}`} 
                sx={{ 
                  backgroundColor: '#e1f5fe', 
                  color: '#0277bd',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '26px',
                  borderRadius: '13px',
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            )}
            {selected.department && (
              <Chip 
                size="small" 
                label={ selected.department?.departmentName || ''} 
                sx={{ 
                  backgroundColor: '#e0f2f1', 
                  color: '#00796b',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '26px',
                  borderRadius: '13px',
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            )}
            {selected.position && (
              <Chip 
                size="small" 
                label={selected.position?.positionName || ''} 
                sx={{ 
                  backgroundColor: '#e8f5e9', 
                  color: '#388e3c',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '26px',
                  borderRadius: '13px',
                  '& .MuiChip-label': { px: 1.5 }
                }}
              />
            )}
            <Chip 
              size="small" 
              label={selected.hasLoginAccess ? 'Login Access' : 'No Login'} 
              sx={{ 
                backgroundColor: selected.hasLoginAccess ? '#e8f5e9' : '#fff3e0',
                color: selected.hasLoginAccess ? '#2e7d32' : '#e65100',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: '26px',
                borderRadius: '13px',
                '& .MuiChip-label': { 
                  px: 1.5,
                  color: 'inherit',
                  fontWeight: 'inherit',
                  fontSize: 'inherit'
                }
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };
  
  return (
    <Box>
      {!selected ? (
        <Autocomplete
          id="employee-search"
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          getOptionLabel={(option) => {
            console.log('Option in getOptionLabel:', option);
            return option.nameEn || `${option.firstNameEn || option.firstName || ''} ${option.lastNameEn || option.lastName || ''}`;
          }}
          options={employees}
          loading={loading}
          onChange={handleEmployeeSelect}
          filterOptions={(options, state) => {
            console.log('Filter options called with:', options.length, 'options');
            return options;
          }}
          noOptionsText={searchTerm.length < 2 ? "Type at least 2 characters" : (loading ? "Searching..." : "No employees found")}
          isOptionEqualToValue={(option, value) => option.id === value.id || option.employeeId === value.employeeId}

          renderInput={(params) => (
            <TextField
              {...params}
              label={`Search Employee${required ? ' *' : ''}`}
              placeholder="Search by name, ID, or email"
              required={required}
              error={required && !selected && error !== ''}
              helperText={error || (required && !selected ? 'Employee selection is required' : '')}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  transition: 'all 0.3s ease',
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 3px rgba(0,131,143,0.1)'
                  },
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#00838f',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00838f',
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: '#546e7a',
                  '&.Mui-focused': {
                    color: '#00838f'
                  }
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  padding: '12px 14px 12px 0',
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? 
                      <CircularProgress 
                        color="primary" 
                        size={20} 
                        sx={{ color: '#00838f' }} 
                      /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                startAdornment: (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      color: '#00838f',
                      mr: 1
                    }}
                  >
                    <MdPersonSearch size={22} />
                  </Box>
                )
              }}
            />
          )}
          renderOption={(props, option) => {
            // Safe logging with error handling
            try {
              console.log('Rendering option:', option);
              console.log('Photo available:', !!option.photo);
              if (option.photo && typeof option.photo === 'string') {
                console.log('Photo starts with data:', option.photo.startsWith('data:'));
                console.log('Photo length:', option.photo.length);
                console.log('Photo preview:', option.photo.substring(0, 50) + '...');
              }
            } catch (error) {
              console.error('Error logging option data:', error);
            }
            return (
              <Paper 
                component="li" 
                {...props} 
                key={`${option.uid || option.employeeId || ''}${option.email || ''}`} 
                elevation={0} 
                sx={{ 
                  mb: 0.8, 
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#f5f9fc'
                  }
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', p: 0.5 }}>
                  {/* Employee basic info row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: '#00838f', 
                        mr: 2,
                        width: 42,
                        height: 42,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {(() => {
                        // Inline base64 thumbnail first — no extra HTTP fetch
                        // per dropdown row. See selected-item branch above.
                        if (option.photoThumbnail) {
                          return (
                            <img
                              src={option.photoThumbnail}
                              alt={`${option.nameEn || option.firstNameEn || 'Employee'} photo`}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          );
                        }

                        // Legacy fallback for rows without an inline thumb.
                        const _normDocType = (t) => String(t || '').toLowerCase().replace(/_/g, '');
                        const minifiedPhotoDoc = option.documents?.find(doc => _normDocType(doc.documentType) === 'photominified');
                        const regularPhotoDoc = option.documents?.find(doc => _normDocType(doc.documentType) === 'photo');
                        const photoDoc = minifiedPhotoDoc || regularPhotoDoc;
                        
                        // Log which photo type we're using for debugging
                        if (minifiedPhotoDoc) {
                          console.log('Using minified photo for better performance');
                        } else if (regularPhotoDoc) {
                          console.log('Falling back to regular photo - minified photo not found');
                        }
                        
                        if (photoDoc && option.uid) {
                          // Temporarily relax UID validation to understand data structure
                          console.log('DEBUG: Dropdown option UID details:', {
                            uid: option.uid,
                            uidType: typeof option.uid,
                            uidLength: option.uid ? option.uid.length : 0,
                            employeeId: option.employeeId,
                            id: option.id
                          });
                          
                          // More permissive UID validation - accept any non-empty string
                          const isValidUID = option.uid && typeof option.uid === 'string' && option.uid.trim().length > 0;
                          
                          if (!isValidUID) {
                            console.warn(`Invalid UID format for employee photo: ${option.uid}. Skipping photo display.`);
                            return (
                              <Box
                                sx={{
                                  backgroundColor: 'red',
                                  color: 'white',
                                  fontSize: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  height: '100%'
                                }}
                              >
                                INVALID UID
                              </Box>
                            );
                          }
                          
                          // Use the minified photo document for display with relative API path
                          const photoUrl = `/v1/users/${option.uid}/documents/photo_minified/download`;
                          console.log('Using photo document URL:', photoUrl, 'for UID:', option.uid);
                          
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}
                            >
                              <AuthenticatedImage
                                src={photoUrl}
                                employeeId={option.uid} // Pass UID as employeeId for proper authentication
                                alt={`${option.nameEn || option.firstNameEn || 'Employee'} photo`}
                                maxSize={minifiedPhotoDoc ? 0 : 80} // Only client-side minify if server minified version not available
                                onLoad={() => {
                                  console.log(`Successfully loaded photo document for employee ${option.employeeId || option.uid}`);
                                }}
                                onError={(err) => {
                                  const employeeId = option.uid || option.employeeId || 'unknown';
                                  console.error(`Error loading photo document for employee ${employeeId}:`, err);
                                }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </Box>
                          );
                        } else {
                          // DEBUG: No fallback - force showing photo or nothing to debug the issue
                          console.error('DEBUG: No photo document found for dropdown employee:', option);
                          console.error('DEBUG: Available documents:', option.documents);
                          return (
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                backgroundColor: '#ff0000', // Red background to make it obvious
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                textAlign: 'center'
                              }}
                            >
                              NO PHOTO
                            </Box>
                          );
                        }
                      })()}
                    </Avatar>
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
                          {option.nameEn || `${option.firstNameEn || ''} ${option.lastNameEn || ''}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                          {option.employeeId || option.uid || ''}
                        </Typography>
                        {option.hasLogin && (
                          <Chip 
                            size="small" 
                            label="Login Access" 
                            color="success" 
                            sx={{ height: 20, fontSize: '0.6rem', fontWeight: 500 }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 1 }}>
                          {option.department?.departmentName || 'No Department'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {option.position?.positionName || ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  {/* Documents section - only show if documents exist */}
                  {option.documents && option.documents.length > 0 && (
                    <Box sx={{ mt: 1, ml: 7, width: '100%' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                        Documents ({option.documents.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {option.documents.slice(0, 3).map((doc, index) => (
                          <Chip
                            key={index}
                            size="small"
                            label={`${doc.documentType || doc.type || 'Document'}: ${doc.fileName || 'File'}`}
                            sx={{ 
                              height: 20,
                              '& .MuiChip-label': { px: 1 } 
                            }}
                            icon={<Description />}
                          />
                        ))}
                        {option.documents.length > 3 && (
                          <Chip
                            size="small"
                            label={`+${option.documents.length - 3} more`}
                            sx={{ 
                              height: 20,
                              '& .MuiChip-label': { px: 1 } 
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          }}
        />
      ) : (
        <Box>
          {renderSelectedEmployee()}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Chip 
              label="Change Employee" 
              onClick={handleClearSelection} 
              sx={{ 
                cursor: 'pointer',
                backgroundColor: '#e1f5fe',
                color: '',
                fontWeight: 600,
                fontSize: '0.8rem',
                height: '32px',
                borderRadius: '16px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: '#b3e5fc',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                },
                '& .MuiChip-label': { 
                  px: 2,
                  color: '#000000',
                  fontWeight: 'inherit',
                  fontSize: 'inherit'
                }
              }}
              icon={<MdPersonSearch size={16} />}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default QuickEmployeeSearch;
